import React from "react";
import "./DevelopmentLoanAgreement.css";

/* =========================================================
 * Types
 * ========================================================= */

export interface Borrower {
    name: string;
    memberNumber: string;
    mobileNumber: string;
    email: string;
    nida: string;
}

export interface LoanDetails {
    reference: string;
    amount: number;
    existingBalance?: number;
    totalExposure?: number;
    purpose: string;
    tenorMonths: number;
    disbursementSchedule: string;
    gracePeriod?: string;
    specificTerms?: string;
    monthlyInstallment: number;
    disbursementAccount: string;
    applicationFee: string;
    insuranceFee: string;
    repaymentFrequency?: string;
    deductionMethod: "HR / Payroll Deduction" | "Standing Order";
    standingOrderAccount?: {
        bank: string;
        accountName: string;
        accountNumber: string;
        deductionDate: string;
    };
    interestRate: number;
    effectiveAnnualInterestRate: number;
    interestComputationMethod: string;
    totalAmountPayable: number;
    latePaymentPenalty: string;
    debtRecoveryFees: string;
}

export interface Guarantor {
    fullName: string;
    memberNumber: string;
    maximumLiability: number;
    mobile: string;
    acceptanceRecord: string;
}

export interface Security {
    borrowerSavings: number;
    guarantors: Guarantor[];
    totalSecurity: number;
}

export interface RepaymentItem {
    number: number;
    dueDate: string;
    openingBalance: number;
    principal: number;
    interest: number;
    fees: number;
    totalInstallment: number;
    closingBalance: number;
}

export interface BorrowerAcceptance {
    accepted: boolean;
    statementVersion: string;
    authenticationMethod: string;
    acceptedAt: string;
    sessionAuditId: string;
    ipDeviceInformation: string;
}

export interface SaccosApproval {
    officerName: string;
    officerUserId: string;
    officerRole: string;
    approvedAt: string;
}

export interface AcceptanceCertificate {
    agreementVersion: string;
    documentHash: string;
    finalAgreementDelivery: string;
    certificateStatus: "VALID" | "REVOKED" | "SUPERSEDED";
}

export interface DevelopmentLoanAgreementProps {
    agreementId: string;

    offerDate: string;
    offerValidFrom: string;
    offerValidUntil: string;

    borrower: Borrower;
    loan: LoanDetails;

    officeDepartment?: string;

    security: Security;

    repaymentSchedule: RepaymentItem[];

    borrowerAcceptance: BorrowerAcceptance;

    saccosApproval: SaccosApproval;

    certificate: AcceptanceCertificate;

    agreementConfig?: {
        agreementTitle?: string;
        lenderName?: string;
        officeDepartment?: string;
        dearBorrowerText?: string;
        acceptanceBoxText?: string;
        buttonText?: string;
        colors?: {
            primary?: string;
            secondary?: string;
            text?: string;
            border?: string;
            lightBorder?: string;
            pageBackground?: string;
            labelBackground?: string;
            valueBackground?: string;
            noticeBackground?: string;
            noticeBorder?: string;
            acceptanceBackground?: string;
            buttonBackground?: string;
        };
    };
}

/* =========================================================
 * Helpers
 * ========================================================= */

const formatMoney = (value?: number) => {
    if (value === undefined || value === null) {
        return "-";
    }

    return new Intl.NumberFormat("en-TZ", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const money = (value?: number) =>
    value === undefined ? "-" : `TZS ${formatMoney(value)}`;

const valueOrNA = (value?: string) => value?.trim() || "N/A";

const CheckBox = ({ checked }: { checked: boolean }) => (
    <span className={`checkbox ${checked ? "checked" : ""}`}>
    {checked ? "✓" : ""}
  </span>
);

/* =========================================================
 * Reusable UI
 * ========================================================= */

interface FieldProps {
    label: string;
    value?: React.ReactNode;
    className?: string;
}

const Field: React.FC<FieldProps> = ({
                                         label,
                                         value,
                                         className = "",
                                     }) => (
    <div className={`field ${className}`}>
        <div className="field-label">{label}</div>
        <div className="field-value">{value || "-"}</div>
    </div>
);

const SectionTitle: React.FC<{
    number?: string;
    title: string;
}> = ({ number, title }) => (
    <div className="section-title">
        {number && <span>{number}</span>}
        <strong>{title}</strong>
    </div>
);

const SubSectionTitle: React.FC<{
    number: string;
    title: string;
}> = ({ number, title }) => (
    <div className="subsection-title">
        <strong>{number}</strong>&nbsp; {title}
    </div>
);

const PageHeader: React.FC<{
    agreementId: string;
    page: number;
    lenderName: string;
    officeDepartment: string;
}> = ({ agreementId, page, lenderName, officeDepartment }) => (
    <div className="document-header">
        <div>
            <strong>{lenderName}</strong>
            <br />
            <span>{officeDepartment}</span>
        </div>

        <div className="header-right">
            <img className="page-header-logo" src="/images/crdb.webp" alt="CRDB Workers SACCOS Ltd" />
            <div>Agreement ID: {agreementId}</div>
            <div>Page {page} of 6</div>
        </div>
    </div>
);

const Bullet: React.FC<{ children: React.ReactNode }> = ({
                                                             children,
                                                         }) => (
    <div className="bullet">
        <span>•</span>
        <div>{children}</div>
    </div>
);

/* =========================================================
 * Main Component
 * ========================================================= */

export const DevelopmentLoanAgreement: React.FC<
    DevelopmentLoanAgreementProps
> = ({
         agreementId,
         offerDate,
         offerValidFrom,
         offerValidUntil,
         borrower,
         loan,
         officeDepartment = "CRDB SACCOS Processing Unit CREDIT & OPERATION",
         security,
         repaymentSchedule,
         borrowerAcceptance,
         saccosApproval,
         certificate,
         agreementConfig,
     }) => {
    const existingBalance = loan.existingBalance ?? 0;

    const totalExposure =
        loan.totalExposure ??
        loan.amount + existingBalance;

    const colors = agreementConfig?.colors ?? {};
    const agreementStyle = {
        '--agreement-primary': colors.primary ?? '#2f7047',
        '--agreement-secondary': colors.secondary ?? '#24527f',
        '--agreement-text': colors.text ?? '#111111',
        '--agreement-border': colors.border ?? '#d9e0e0',
        '--agreement-light-border': colors.lightBorder ?? '#b9c6c0',
        '--agreement-page-background': colors.pageBackground ?? '#ffffff',
        '--agreement-label-background': colors.labelBackground ?? '#eaf2ed',
        '--agreement-value-background': colors.valueBackground ?? '#eaf1f6',
        '--agreement-notice-background': colors.noticeBackground ?? '#fff5d6',
        '--agreement-notice-border': colors.noticeBorder ?? '#e4c86a',
        '--agreement-acceptance-background': colors.acceptanceBackground ?? '#eaf1f6',
        '--agreement-button-background': colors.buttonBackground ?? '#24527f',
    } as React.CSSProperties;

    return (
        <div className="loan-agreement" style={agreementStyle}>

            {/* =====================================================
          PAGE 1
      ====================================================== */}

            <section className="agreement-page">
                <PageHeader
                    agreementId={agreementId}
                    page={1}
                    lenderName={agreementConfig?.lenderName ?? 'CRDB WORKERS SACCOS LTD'}
                    officeDepartment={agreementConfig?.officeDepartment ?? officeDepartment}
                />
                {/*<div className="logo-container">*/}
                {/*    <img className="main-logo" src="/images/crdb.webp" alt="CRDB Workers SACCOS Ltd" />*/}
                {/*</div>*/}

                <div className="document-title">
                    <img className="document-logo" src="/images/crdb.webp" alt="CRDB Workers SACCOS Ltd" />
                    <h1>{agreementConfig?.agreementTitle ?? 'DEVELOPMENT LOAN OFFER AND ELECTRONIC ACCEPTANCE'}</h1>
                    <h2>AGREEMENT</h2>
                    <div>{agreementConfig?.lenderName ?? 'CRDB SACCOS'} | CREDIT &amp; OPERATION</div>
                </div>

                <div className="important-notice">
                    <strong>IMPORTANT:</strong> This template is designed for
                    electronic execution. It removes wet-ink signature lines but
                    requires authenticated electronic signatures and an auditable
                    acceptance record.
                </div>

                <div className="info-grid">
                    <Field label="Agreement ID" value={agreementId} />
                    <Field label="Offer Date" value={offerDate} />

                    <Field
                        label="Borrower Name"
                        value={borrower.name}
                    />

                    <Field
                        label="Member No."
                        value={borrower.memberNumber}
                    />

                    <Field
                        label="Mobile Number"
                        value={borrower.mobileNumber}
                    />

                    <Field
                        label="Email Address"
                        value={borrower.email}
                    />

                    <Field label="NIDA" value={borrower.nida} />

                    <Field
                        label="Loan Reference"
                        value={loan.reference}
                    />

                    <Field
                        label="Offer Valid From"
                        value={offerValidFrom}
                    />

                    <Field
                        label="Offer Valid Until"
                        value={offerValidUntil}
                    />

                    <Field
                        label="Office / Department"
                        value={agreementConfig?.officeDepartment ?? officeDepartment}
                        className="full-width"
                    />
                </div>

                <div className="body-section">
                    <p>
                        {agreementConfig?.dearBorrowerText ?? 'CRDB Workers SACCOS Ltd (the “SACCOS” or “Lender”) has approved the Development Loan described below for the member identified above (the “Borrower”). This Agreement consists of the Facility Terms, the Standard Terms and Conditions, the Security Schedule, the Repayment Schedule and the Electronic Acceptance Certificate, which together form the complete loan agreement.'}
                    </p>

                    <p>
                        CRDB Workers SACCOS Ltd (the “SACCOS” or “Lender”) has
                        approved the Development Loan described below for the
                        member identified above (the “Borrower”). This Agreement
                        consists of the Facility Terms, the Standard Terms and
                        Conditions, the Security Schedule, the Repayment Schedule
                        and the Electronic Acceptance Certificate, which together
                        form the complete loan agreement.
                    </p>
                </div>

                <SectionTitle number="1.0" title="FACILITY TERMS" />

                <div className="terms-table">
                    <div className="term-row">
                        <div className="term-number">1.1</div>
                        <div className="term-label">Name of Borrower</div>
                        <div className="term-value">{borrower.name}</div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.2</div>
                        <div className="term-label">Facility Type</div>
                        <div className="term-value">
                            Development Loan
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.3</div>
                        <div className="term-label">Facility Amount</div>
                        <div className="term-value">
                            <div>
                                <strong>New Development Loan Amount</strong>{" "}
                                {money(loan.amount)}
                            </div>
                            <div>
                                <strong>
                                    Existing Loan Balance / Exposure
                                </strong>{" "}
                                {money(existingBalance)}
                            </div>
                            <div>
                                <strong>Total Loan Exposure</strong>{" "}
                                {money(totalExposure)}
                            </div>
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.4</div>
                        <div className="term-label">Loan Purpose</div>
                        <div className="term-value">{loan.purpose}</div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.5</div>
                        <div className="term-label">Security</div>
                        <div className="term-value">
                            As detailed in Schedule 1 - Security
                            (Borrower and Guarantor Savings)
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.6</div>
                        <div className="term-label">Facility Tenor</div>
                        <div className="term-value">
                            {loan.tenorMonths} months
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.7</div>
                        <div className="term-label">
                            Disbursement Schedule
                        </div>
                        <div className="term-value">
                            {loan.disbursementSchedule}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.8</div>
                        <div className="term-label">
                            Grace Period (if any)
                        </div>
                        <div className="term-value">
                            {valueOrNA(loan.gracePeriod)}
                        </div>
                    </div>
                </div>
            </section>

            {/* =====================================================
          PAGE 2
      ====================================================== */}

            <section className="agreement-page">
                <PageHeader
                    agreementId={agreementId}
                    page={2}
                    lenderName={agreementConfig?.lenderName ?? 'CRDB WORKERS SACCOS LTD'}
                    officeDepartment={agreementConfig?.officeDepartment ?? officeDepartment}
                />

                <div className="terms-table">
                    <div className="term-row">
                        <div className="term-number">1.9</div>
                        <div className="term-label">
                            Specific Terms and Conditions
                            (if any)
                        </div>
                        <div className="term-value">
                            {valueOrNA(loan.specificTerms)}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.10</div>
                        <div className="term-label">
                            Monthly Loan Instalment
                        </div>
                        <div className="term-value">
                            {money(loan.monthlyInstallment)}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.11</div>
                        <div className="term-label">
                            Repayment Schedule
                        </div>
                        <div className="term-value">
                            As set out in Annex 1 - System-Generated
                            Repayment Schedule
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.12</div>
                        <div className="term-label">
                            Disbursement / Settlement Account
                        </div>
                        <div className="term-value">
                            {loan.disbursementAccount}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.13</div>
                        <div className="term-label">
                            Loan Application Fee
                        </div>
                        <div className="term-value">
                            {loan.applicationFee}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.14</div>
                        <div className="term-label">
                            Loan Insurance Fee
                        </div>
                        <div className="term-value">
                            {loan.insuranceFee}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.15</div>
                        <div className="term-label">
                            Frequency of Loan Repayment
                        </div>
                        <div className="term-value">
                            {loan.repaymentFrequency ?? "Monthly"}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.16</div>
                        <div className="term-label">
                            Repayment Deduction Selection
                        </div>

                        <div className="term-value">
                            <div className="checkbox-option">
                                <CheckBox
                                    checked={
                                        loan.deductionMethod ===
                                        "HR / Payroll Deduction"
                                    }
                                />
                                HR / Payroll Deduction
                            </div>

                            <div className="checkbox-option">
                                <CheckBox
                                    checked={
                                        loan.deductionMethod ===
                                        "Standing Order"
                                    }
                                />
                                Standing Order
                            </div>

                            {loan.deductionMethod ===
                                "Standing Order" &&
                                loan.standingOrderAccount && (
                                    <div className="standing-order">
                                        <strong>
                                            Standing Order Account:
                                        </strong>

                                        <div>
                                            Bank:{" "}
                                            {
                                                loan.standingOrderAccount.bank
                                            }
                                        </div>

                                        <div>
                                            Account Name:{" "}
                                            {
                                                loan.standingOrderAccount
                                                    .accountName
                                            }
                                        </div>

                                        <div>
                                            Account No.:{" "}
                                            {
                                                loan.standingOrderAccount
                                                    .accountNumber
                                            }
                                        </div>

                                        <div>
                                            Deduction Date:{" "}
                                            {
                                                loan.standingOrderAccount
                                                    .deductionDate
                                            }
                                        </div>

                                        <small>
                                            300 TZS charge for every Standing
                                            Order Deduction.
                                        </small>
                                    </div>
                                )}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.17</div>
                        <div className="term-label">
                            Nominal / Stated Annual Interest Rate
                        </div>
                        <div className="term-value">
                            {loan.interestRate}% per annum
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.18</div>
                        <div className="term-label">
                            Effective Annual Interest Rate
                            <br />
                            (including fees)
                        </div>
                        <div className="term-value">
                            {loan.effectiveAnnualInterestRate}% per annum
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.19</div>
                        <div className="term-label">
                            Interest Computation Method
                        </div>
                        <div className="term-value">
                            {loan.interestComputationMethod}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.20</div>
                        <div className="term-label">
                            Total Amount Payable over the Loan Term
                        </div>
                        <div className="term-value">
                            {money(loan.totalAmountPayable)}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.21</div>
                        <div className="term-label">
                            Late Payment Penalty
                        </div>
                        <div className="term-value">
                            {loan.latePaymentPenalty}
                        </div>
                    </div>

                    <div className="term-row">
                        <div className="term-number">1.22</div>
                        <div className="term-label">
                            Debt Recovery Fees, Charges or Expenses
                            in Case of Default
                        </div>
                        <div className="term-value">
                            {loan.debtRecoveryFees}
                        </div>
                    </div>
                </div>

                <SectionTitle
                    number="2.0"
                    title="ELECTRONIC ACCEPTANCE"
                />

                <SubSectionTitle
                    number="2.1"
                    title="Borrower Acceptance"
                />

                <p>
                    The Borrower shall accept this Agreement electronically
                    through the CRDB SACCOS member portal or another approved
                    digital channel. No handwritten signature is required.
                    Acceptance is valid only when the Borrower:
                </p>

                <Bullet>
                    Tick the unchecked acceptance box shown below and
                    clicks the “ACCEPT LOAN OFFER” button;
                </Bullet>

                <Bullet>
                    successfully completes identity verification using
                    the registered member credentials and a one-time
                    password (OTP), transaction PIN or other approved
                    authentication method; and
                </Bullet>

                <Bullet>
                    receives an electronic confirmation and a
                    downloadable copy of the accepted Agreement.
                </Bullet>

                <div className="acceptance-box">
                    <CheckBox checked={borrowerAcceptance.accepted} />

                    <span>
            {agreementConfig?.acceptanceBoxText ?? 'I have read, understood and accept this Development Loan Agreement, including all schedules and disclosures. I intend my authenticated checkbox selection and confirmation to constitute my electronic signature and to bind me to the Agreement.'}
          </span>
                </div>

                <button
                    type="button"
                    className="accept-button"
                >
                    {agreementConfig?.buttonText ?? 'ACCEPT LOAN OFFER'}
                </button>

                <SubSectionTitle
                    number="2.2"
                    title="SACCOS Electronic Approval"
                />

                <p>
                    The SACCOS shall approve and issue this Agreement through
                    an authorised system workflow. The system shall record
                    the approving officer’s identity, role, approval date and
                    time, loan reference and Agreement version. The SACCOS
                    approval record and the Borrower’s authenticated
                    acceptance together constitute the electronic signatures
                    of both parties. No wet-ink signature blocks are
                    required.
                </p>

                <SubSectionTitle
                    number="2.3"
                    title="Effective Time and Electronic Evidence"
                />

                <p>
                    This Agreement becomes effective when the Borrower’s
                    authenticated acceptance is successfully recorded against
                    an Agreement already approved and issued by the SACCOS.
                    The Electronic Acceptance Certificate in Schedule 3,
                    together with the audit trail, shall form part of this
                    Agreement and may be used to establish the identity,
                    intention, date, time, version and integrity of the
                    transaction.
                </p>
            </section>

            {/* =====================================================
          PAGE 3
      ====================================================== */}

            <section className="agreement-page">
                <PageHeader
                    agreementId={agreementId}
                    page={3}
                    lenderName={agreementConfig?.lenderName ?? 'CRDB WORKERS SACCOS LTD'}
                    officeDepartment={agreementConfig?.officeDepartment ?? officeDepartment}
                />

                <SectionTitle
                    number="3.0"
                    title="CONDITIONS BEFORE DISBURSEMENT"
                />

                <p>
                    The SACCOS may disburse the Facility only after all
                    applicable conditions have been satisfied, including:
                </p>

                <Bullet>
                    The Borrower remains an eligible member and all
                    information provided is complete and accurate;
                </Bullet>

                <Bullet>
                    The loan has been approved through the SACCOS credit
                    approval process;
                </Bullet>

                <Bullet>
                    The required borrower savings, guarantor savings and
                    any other approved security have been verified,
                    documented and made available in accordance with the
                    lending policy;
                </Bullet>

                <Bullet>
                    Each guarantor has received the required liability
                    disclosure and has separately accepted the guarantee
                    electronically or in another legally valid form;
                </Bullet>

                <Bullet>
                    The selected deduction instruction has been activated;
                </Bullet>

                <Bullet>
                    All disclosed application and insurance fees have been
                    paid or authorized for deduction; and
                </Bullet>

                <Bullet>
                    Any specific condition stated under clause 1.9 has been
                    fulfilled.
                </Bullet>

                <SectionTitle
                    number="4.0"
                    title="DISBURSEMENT AND USE OF THE LOAN"
                />

                <SubSectionTitle
                    number="4.1"
                    title=""
                />

                <p>
                    The SACCOS shall disburse the Facility in the manner
                    shown in clauses 1.7 and 1.12. Any deduction from the
                    gross Facility amount shall be limited to fees and
                    charges expressly disclosed in this Agreement.
                </p>

                <SubSectionTitle
                    number="4.2"
                    title=""
                />

                <p>
                    The Borrower shall state the Facility in clause 1.4
                </p>

                <SubSectionTitle
                    number="4.3"
                    title=""
                />

                <p>
                    The Borrower confirms that all statements and documents
                    submitted in connection with the Facility are true,
                    complete and not misleading in any material respect.
                </p>

                <SectionTitle
                    number="5.0"
                    title="INTEREST, FEES AND CHARGES"
                />

                <SubSectionTitle
                    number="5.1"
                    title=""
                />

                <p>
                    Interest shall be calculated using the method and rate
                    stated in clauses 1.17 to 1.19 and reflected in the
                    Repayment Schedule.
                </p>

                <SubSectionTitle
                    number="5.2"
                    title=""
                />

                <p>
                    The Borrower shall pay only the fees, charges, penalties
                    and recovery expenses expressly stated in this Agreement
                    or subsequently agreed in a valid written or electronic
                    amendment accepted by both parties.
                </p>

                <SectionTitle number="6.0" title="REPAYMENT" />

                <SubSectionTitle number="6.1" title="" />

                <p>
                    The Borrower shall repay the Facility by the instalments
                    and due dates shown in Annex 1. The selected deduction
                    channel under clause 1.16 is a collection method and
                    does not remove the Borrower’s personal obligation to
                    ensure that every instalment is paid in full and on time.
                </p>

                <SubSectionTitle number="6.2" title="" />

                <p>
                    If an HR/payroll deduction or standing order is delayed,
                    rejected, reduced or stopped, the Borrower shall
                    immediately pay the affected instalment through an
                    alternative channel approved by the SACCOS and shall
                    promptly restore the deduction instruction.
                </p>

                <SubSectionTitle number="6.3" title="" />

                <p>
                    Where a standing order is selected, the Borrower shall
                    authorizes the recurring deduction of the instalment
                    stated in clause 1.10 with service charge of TZS 300 on
                    the agreed deduction date and undertakes to maintain
                    sufficient funds in the nominated account.
                </p>

                <SubSectionTitle number="6.4" title="" />

                <p>
                    The Borrower may repay the Facility early, in whole or in
                    part, on a business day without a prepayment penalty.
                    Interest shall not be charged for the remaining period
                    after full early repayment, subject to settlement of
                    accrued amounts and properly disclosed charges up to the
                    settlement date.
                </p>

                <SectionTitle
                    number="7.0"
                    title="SECURITY AND GUARANTEES"
                />

                <SubSectionTitle number="7.1" title="" />

                <p>
                    The Facility is secured by the savings amounts stated in
                    Schedule 1 and any other security expressly approved and
                    documented by the SACCOS. Membership shares shall not be
                    treated as loan collateral where prohibited by applicable
                    law.
                </p>

                <SubSectionTitle number="7.2" title="" />

                <p>
                    The Borrower and each guarantor authorize the SACCOS,
                    after default and the required notice have been applied,
                    to use the specifically pledged savings, terminal
                    benefits, salary, voluntary shares and any interest
                    gained from CRDB SACCOS toward the overdue debt in
                    accordance with this Agreement, the lending policy and
                    applicable law.
                </p>
            </section>

            {/* =====================================================
          PAGE 4
      ====================================================== */}

            <section className="agreement-page">
                <PageHeader
                    agreementId={agreementId}
                    page={4}
                    lenderName={agreementConfig?.lenderName ?? 'CRDB WORKERS SACCOS LTD'}
                    officeDepartment={agreementConfig?.officeDepartment ?? officeDepartment}
                />

                <SubSectionTitle number="7.3" title="" />

                <p>
                    Each guarantor’s liability shall not exceed the guaranteed
                    amount stated in Schedule 1, together with only those
                    related amounts lawfully recoverable under the guarantee.
                    The SACCOS shall provide each guarantor with a separate
                    written or electronic disclosure of the nature and extent
                    of the liability before acceptance.
                </p>

                <SubSectionTitle number="7.4" title="" />

                <p>
                    If security belongs to a person other than the Borrower,
                    that person must provide valid written or electronic
                    authorization before the Facility is disbursed.
                </p>

                <SectionTitle
                    number="8.0"
                    title="DEFAULT AND RECOVERY"
                />

                <SubSectionTitle number="8.1" title="" />

                <p>
                    An event of default occurs if the Borrower fails to pay a
                    full instalment when due, provides materially false
                    information, misuses the Facility in a manner that
                    materially increases the credit risk, unlawfully disposes
                    of pledged security, or commits another material breach
                    and fails to remedy it within any applicable notice period.
                </p>

                <SubSectionTitle number="8.2" title="" />

                <p>
                    Before commencing debt collection or enforcing security,
                    the SACCOS shall issue the Borrower with at least fourteen
                    (14) days’ written or electronic notice, unless immediate
                    action is lawfully required to preserve security or
                    prevent fraud.
                </p>

                <SubSectionTitle number="8.3" title="" />

                <p>
                    Following default and expiry of the required notice, the
                    SACCOS may apply pledged savings, demand payment from
                    guarantors within their disclosed liability, commence
                    lawful recovery proceedings and recover only the fees,
                    charges or expenses disclosed in this Agreement.
                </p>

                <SubSectionTitle number="8.4" title="" />

                <p>
                    The SACCOS and any appointed debt collector shall conduct
                    recovery fairly and shall not harass, threaten, abuse,
                    oppress or use unlawful means against the Borrower, a
                    guarantor or another person.
                </p>

                <SectionTitle
                    number="9.0"
                    title="LOAN INSURANCE"
                />

                <SubSectionTitle number="9.1" title="" />

                <p>
                    Loan insurance applies, as it is stated in clause 1.14
                </p>

                <SubSectionTitle number="9.2" title="" />

                <p>
                    Any insurance benefit shall be applied to the outstanding
                    debt in accordance with the policy. The Borrower or estate
                    remains responsible for any amount not covered because of
                    exclusions, limits, non-disclosure or another lawful
                    reason under the policy.
                </p>

                <SectionTitle
                    number="10.0"
                    title="ELECTRONIC COMMUNICATIONS AND RECORD RETENTION"
                />

                <SubSectionTitle number="10.1" title="" />

                <p>
                    The Borrower consents to receive the Agreement, repayment
                    schedule, notices, statements, reminders and confirmations
                    through the member portal, registered email address,
                    registered mobile number or another agreed electronic
                    channel.
                </p>

                <SubSectionTitle number="10.2" title="" />

                <p>
                    The SACCOS shall make the accepted Agreement available in
                    a format that the Borrower can download, retain and
                    reproduce for subsequent reference.
                </p>

                <SubSectionTitle number="10.3" title="" />

                <p>
                    The Borrower shall promptly update contact details and
                    remains responsible for protecting portal credentials.
                    The Borrower shall immediately report suspected
                    unauthorized access.
                </p>

                <SubSectionTitle number="10.4" title="" />

                <p>
                    The SACCOS shall retain the accepted Agreement and
                    relevant audit records in accordance with applicable law,
                    record-retention rules and its approved policies.
                </p>

                <SectionTitle
                    number="11.0"
                    title="PERSONAL DATA AND INFORMATION SHARING"
                />

                <SubSectionTitle number="11.1" title="" />

                <p>
                    The SACCOS may collect and process personal data necessary
                    to assess, approve, administer, insure, monitor and
                    recover the Facility; prevent fraud; meet legal and
                    regulatory obligations; and maintain the member
                    relationship.
                </p>

                <SubSectionTitle number="11.2" title="" />

                <p>
                    Subject to applicable law and the SACCOS Privacy Notice,
                    relevant information may be shared with the Borrower’s
                    employer or HR unit, guarantors, insurers, payment service
                    providers, credit reference bureaus, regulators, auditors,
                    professional advisers and lawful recovery service
                    providers, only to the extent necessary for the stated
                    purpose.
                </p>

                <SubSectionTitle number="11.3" title="" />

                <p>
                    Marketing use of personal data shall require a separate
                    lawful basis or consent and shall not be bundled with
                    acceptance of this loan Agreement.
                </p>

                <SectionTitle
                    number="12.0"
                    title="COMPLAINTS AND DISPUTE RESOLUTION"
                />

                <SubSectionTitle number="12.1" title="" />

                <p>
                    A complaint may be submitted through: SACCOS email,
                    letter to the SACCOS office. The SACCOS shall acknowledge,
                    investigate and respond within one week.
                </p>

                <SubSectionTitle number="12.2" title="" />

                <p>
                    If the complaint is not resolved, either party may use
                    any escalation, mediation, regulatory or judicial
                    mechanism available under the Microfinance Act and laws
                    of the United Republic of Tanzania.
                </p>
            </section>

            {/* =====================================================
          PAGE 5
      ====================================================== */}

            <section className="agreement-page">
                <PageHeader
                    agreementId={agreementId}
                    page={5}
                    lenderName={agreementConfig?.lenderName ?? 'CRDB WORKERS SACCOS LTD'}
                    officeDepartment={agreementConfig?.officeDepartment ?? officeDepartment}
                />

                <SectionTitle number="13.0" title="GENERAL TERMS" />

                <SubSectionTitle number="13.1" title="" />

                <p>
                    This Agreement, including its schedules and electronic
                    records, constitutes the entire agreement concerning the
                    Facility and replaces earlier representations relating to
                    the same Facility.
                </p>

                <SubSectionTitle number="13.2" title="" />

                <p>
                    An amendment is valid only if recorded in writing or
                    electronic form, clearly identifies the changed terms and
                    is approved and accepted through a legally valid process.
                </p>

                <SubSectionTitle number="13.3" title="" />

                <p>
                    A delay or failure to exercise a right does not waive
                    that right. If any provision is held invalid, the
                    remaining provisions continue to apply to the extent
                    permitted by law.
                </p>

                <SubSectionTitle number="13.4" title="" />

                <p>
                    This Agreement is governed by the laws of the United
                    Republic of Tanzania and shall be interpreted consistently
                    with applicable SACCOS policy, Microfinance Act,
                    electronic transactions and personal data protection
                    requirements.
                </p>

                <SectionTitle
                    title="SCHEDULE 1 - SECURITY"
                />

                <p>
                    The security below is based on the approved borrower and
                    guarantor savings. Amounts must be verified by the system
                    immediately before disbursement and must not exceed the
                    amount lawfully available for pledge.
                </p>

                <table className="data-table">
                    <thead>
                    <tr>
                        <th>No.</th>
                        <th>Description</th>
                        <th>Amount (TZS)</th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr>
                        <td>1</td>
                        <td>Borrower Savings</td>
                        <td>{formatMoney(security.borrowerSavings)}</td>
                    </tr>

                    {security.guarantors.map(
                        (guarantor, index) => (
                            <tr key={guarantor.memberNumber}>
                                <td>{index + 2}</td>
                                <td>
                                    Guarantor {index + 1} Savings
                                </td>
                                <td>
                                    {formatMoney(
                                        guarantor.maximumLiability
                                    )}
                                </td>
                            </tr>
                        )
                    )}

                    <tr className="total-row">
                        <td colSpan={2}>TOTAL SECURITY</td>
                        <td>
                            {formatMoney(security.totalSecurity)}
                        </td>
                    </tr>
                    </tbody>
                </table>

                <h3 className="subheading">
                    Guarantor Details and Electronic Acceptance
                </h3>

                <table className="data-table guarantor-table">
                    <thead>
                    <tr>
                        <th>#</th>
                        <th>Full Name</th>
                        <th>Member No.</th>
                        <th>Maximum Liability</th>
                        <th>Registered Mobile</th>
                        <th>Acceptance Record</th>
                    </tr>
                    </thead>

                    <tbody>
                    {security.guarantors.map(
                        (guarantor, index) => (
                            <tr key={guarantor.memberNumber}>
                                <td>{index + 1}</td>
                                <td>{guarantor.fullName}</td>
                                <td>{guarantor.memberNumber}</td>
                                <td>
                                    {money(guarantor.maximumLiability)}
                                </td>
                                <td>{guarantor.mobile}</td>
                                <td>{guarantor.acceptanceRecord}</td>
                            </tr>
                        )
                    )}
                    </tbody>
                </table>

                <div className="guarantor-confirmation">
                    Each guarantor confirms: “I have received and understood
                    the guarantee disclosure, including the Borrower’s name,
                    loan number, loan amount, date, and my maximum liability.
                    By completing the authenticated acceptance process, I
                    agree to the guarantee and intend the acceptance record to
                    constitute my electronic signature.”
                </div>

                <SectionTitle
                    title="ANNEX 1 - REPAYMENT SCHEDULE"
                />

                <p>
                    The system shall generate and attach the complete
                    repayment schedule before the Borrower accepts the
                    Agreement. The schedule must show each instalment, its due
                    date, opening balance, principal, interest, fees, total
                    instalment and closing balance, together with the sum of
                    all payments.
                </p>

                <div className="repayment-table-wrapper">
                    <table className="data-table repayment-table">
                        <thead>
                        <tr>
                            <th>No.</th>
                            <th>Due Date</th>
                            <th>Opening Balance</th>
                            <th>Principal</th>
                            <th>Interest</th>
                            <th>Fees</th>
                            <th>Total Instalment</th>
                            <th>Closing Balance</th>
                        </tr>
                        </thead>

                        <tbody>
                        {repaymentSchedule.map((item) => (
                            <tr key={item.number}>
                                <td>{item.number}</td>
                                <td>{item.dueDate}</td>
                                <td>
                                    {formatMoney(item.openingBalance)}
                                </td>
                                <td>
                                    {formatMoney(item.principal)}
                                </td>
                                <td>
                                    {formatMoney(item.interest)}
                                </td>
                                <td>
                                    {formatMoney(item.fees)}
                                </td>
                                <td>
                                    {formatMoney(item.totalInstallment)}
                                </td>
                                <td>
                                    {formatMoney(item.closingBalance)}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* =====================================================
          PAGE 6
      ====================================================== */}

            <section className="agreement-page">
                <PageHeader
                    agreementId={agreementId}
                    page={6}
                    lenderName={agreementConfig?.lenderName ?? 'CRDB WORKERS SACCOS LTD'}
                    officeDepartment={agreementConfig?.officeDepartment ?? officeDepartment}
                />

                <SectionTitle
                    title="SCHEDULE 3 - ELECTRONIC ACCEPTANCE CERTIFICATE"
                />

                <p>
                    This certificate is generated automatically and attached
                    to the final Agreement after all required electronic
                    acceptances have been completed.
                </p>

                <div className="certificate-grid">
                    <Field
                        label="Agreement / Document ID"
                        value={agreementId}
                    />

                    <Field
                        label="Loan Reference Number"
                        value={loan.reference}
                    />

                    <Field
                        label="Agreement Version"
                        value={certificate.agreementVersion}
                    />

                    <Field
                        label="Document Hash / Integrity Value"
                        value={certificate.documentHash}
                    />

                    <Field
                        label="Borrower Member Number"
                        value={borrower.memberNumber}
                    />

                    <Field
                        label="Borrower Registered Mobile / Email"
                        value={`${borrower.mobileNumber} / ${borrower.email}`}
                    />

                    <Field
                        label="Borrower Acceptance Checkbox"
                        value={
                            borrowerAcceptance.accepted
                                ? `YES - ${borrowerAcceptance.statementVersion}`
                                : "NO"
                        }
                    />

                    <Field
                        label="Borrower Authentication Method"
                        value={
                            borrowerAcceptance.authenticationMethod
                        }
                    />

                    <Field
                        label="Borrower Acceptance Date and Time"
                        value={borrowerAcceptance.acceptedAt}
                    />

                    <Field
                        label="Borrower Session / Audit Trail ID"
                        value={borrowerAcceptance.sessionAuditId}
                    />

                    <Field
                        label="Borrower IP / Device Information"
                        value={
                            borrowerAcceptance.ipDeviceInformation
                        }
                    />

                    <Field
                        label="SACCOS Approving Officer"
                        value={`${saccosApproval.officerName} / ${saccosApproval.officerUserId} / ${saccosApproval.officerRole}`}
                    />

                    <Field
                        label="SACCOS Approval Date and Time"
                        value={saccosApproval.approvedAt}
                    />

                    {security.guarantors.map(
                        (guarantor, index) => (
                            <Field
                                key={guarantor.memberNumber}
                                label={`Guarantor ${
                                    index + 1
                                } Acceptance Record`}
                                value={guarantor.acceptanceRecord}
                            />
                        )
                    )}

                    <Field
                        label="Final Agreement Delivery"
                        value={certificate.finalAgreementDelivery}
                    />

                    <Field
                        label="Certificate Status"
                        value={
                            <span
                                className={`certificate-status ${certificate.certificateStatus.toLowerCase()}`}
                            >
                {certificate.certificateStatus}
              </span>
                        }
                    />
                </div>

                <div className="integrity-statement">
                    <h3>Electronic Record Integrity Statement</h3>

                    <p>
                        The SACCOS system shall preserve the accepted Agreement,
                        its version, acceptance statement, authentication
                        results, timestamps and audit identifiers in a manner
                        designed to detect unauthorised alteration. The
                        Borrower must be able to obtain a copy of the complete
                        accepted Agreement for subsequent reference.
                    </p>
                </div>

                <div className="end-of-agreement">
                    END OF AGREEMENT
                </div>
            </section>
        </div>
    );
};

export default DevelopmentLoanAgreement;